import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, makeStyles } from '@material-ui/core'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, ContentState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import { RICH_TEXT_SPACING_OPTIONS } from '../utils/introRichText'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

const useStyles = makeStyles((theme) => ({
  richEditorLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  richEditorWrapper: {
    border: '1px solid #c4c4c4',
    borderRadius: 4,
    background: '#fff',
  },
  richEditorToolbar: {
    border: 'none',
    borderBottom: '1px solid #e6e6e6',
    marginBottom: 0,
    padding: '4px 6px',
  },
  richEditorBody: {
    minHeight: 190,
    maxHeight: 360,
    overflowY: 'auto',
    padding: '0 12px',
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    fontSize: 14,
    lineHeight: 1.6,
  },
}))

const SPACING_STYLE_KEYS = ['line-height', 'margin-bottom']
const spacingByValue = RICH_TEXT_SPACING_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option
  return acc
}, {})

const spacingValueForData = (data) => {
  const lineHeight = data?.get ? data.get('line-height') : data?.['line-height']
  const marginBottom = data?.get ? data.get('margin-bottom') : data?.['margin-bottom']
  const match = RICH_TEXT_SPACING_OPTIONS.find((option) => {
    const style = option.style || {}
    return (style['line-height'] || '') === (lineHeight || '') &&
      (style['margin-bottom'] || '') === (marginBottom || '')
  })
  return match?.value || 'normal'
}

const spacingDataFromElement = (el) => {
  const normalized = {}
  const lineHeight = String(el.style?.lineHeight || '').toLowerCase().replace(/\s+/g, '')
  const marginBottom = String(el.style?.marginBottom || '').toLowerCase().replace(/\s+/g, '')
  RICH_TEXT_SPACING_OPTIONS.forEach((option) => {
    const style = option.style || {}
    if (style['line-height'] && style['line-height'] === lineHeight) normalized['line-height'] = lineHeight
    if (style['margin-bottom'] && style['margin-bottom'] === marginBottom) normalized['margin-bottom'] = marginBottom
  })
  return normalized
}

const spacingBlocksFromHtml = (html) => {
  if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return []
  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstChild
  if (!root) return []
  return Array.from(root.querySelectorAll('p,h3,h4,li'))
    .map(spacingDataFromElement)
}

const applyHtmlBlockSpacing = (contentState, html) => {
  const spacingBlocks = spacingBlocksFromHtml(html)
  if (!spacingBlocks.length) return contentState

  let index = 0
  const blockMap = contentState.getBlockMap().map((block) => {
    const spacing = spacingBlocks[index++]
    if (!spacing || !Object.keys(spacing).length) return block
    let data = block.getData()
    Object.entries(spacing).forEach(([key, value]) => {
      data = data.set(key, value)
    })
    return block.set('data', data)
  })
  return contentState.merge({ blockMap })
}

const selectedBlockKeys = (editorState) => {
  const selection = editorState.getSelection()
  const startKey = selection.getStartKey()
  const endKey = selection.getEndKey()
  const keys = []
  let inRange = false
  editorState.getCurrentContent().getBlockMap().forEach((block, key) => {
    if (key === startKey) inRange = true
    if (inRange) keys.push(key)
    if (key === endKey) inRange = false
  })
  return keys
}

const currentSpacingValue = (editorState) => {
  const keys = selectedBlockKeys(editorState)
  if (!keys.length) return 'normal'
  const content = editorState.getCurrentContent()
  const values = keys.map(key => spacingValueForData(content.getBlockForKey(key).getData()))
  return values.every(value => value === values[0]) ? values[0] : 'normal'
}

const applySpacing = (editorState, spacingValue) => {
  const option = spacingByValue[spacingValue] || spacingByValue.normal
  const spacingStyle = option.style || {}
  const keys = selectedBlockKeys(editorState)
  const content = editorState.getCurrentContent()
  const blockMap = content.getBlockMap().map((block, key) => {
    if (!keys.includes(key)) return block
    let data = block.getData()
    SPACING_STYLE_KEYS.forEach(styleKey => { data = data.delete(styleKey) })
    Object.entries(spacingStyle).forEach(([styleKey, value]) => { data = data.set(styleKey, value) })
    return block.set('data', data)
  })
  return EditorState.push(editorState, content.merge({ blockMap }), 'change-block-data')
}

const SpacingControl = ({ editorState, onChange }) => {
  if (!editorState) return null
  return (
    <select
      title="Paragraph spacing"
      aria-label="Paragraph spacing"
      value={currentSpacingValue(editorState)}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => onChange(applySpacing(editorState, e.target.value))}
      style={{
        height: 34,
        margin: '0 4px 6px 0',
        padding: '0 8px',
        border: '1px solid #f1f1f1',
        borderRadius: 2,
        background: '#fff',
        color: '#333',
        fontSize: 13,
      }}
    >
      {RICH_TEXT_SPACING_OPTIONS.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  )
}

const editorStateFromHtml = (html) => {
  const blocksFromHtml = htmlToDraft(html || '')
  if (!blocksFromHtml) return EditorState.createEmpty()
  const contentState = applyHtmlBlockSpacing(ContentState.createFromBlockArray(
    blocksFromHtml.contentBlocks,
    blocksFromHtml.entityMap
  ), html)
  return EditorState.createWithContent(contentState)
}

const htmlFromEditorState = (editorState) => {
  return draftToHtml(convertToRaw(editorState.getCurrentContent()))
}

const RichTextField = ({ label = 'Body', value, onChange }) => {
  const classes = useStyles()
  const initialHtml = value || ''
  const lastEmittedHtml = useRef(initialHtml)
  const [editorState, setEditorState] = useState(() => editorStateFromHtml(initialHtml))

  useEffect(() => {
    const nextValue = value || ''
    if (nextValue !== lastEmittedHtml.current) {
      lastEmittedHtml.current = nextValue
      setEditorState(editorStateFromHtml(nextValue))
    }
  }, [value])

  const handleEditorStateChange = (nextState) => {
    setEditorState(nextState)
    const nextHtml = htmlFromEditorState(nextState)
    lastEmittedHtml.current = nextHtml
    onChange(nextHtml)
  }

  return (
    <Box>
      <Typography className={classes.richEditorLabel}>{label}</Typography>
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorStateChange}
        wrapperClassName={classes.richEditorWrapper}
        toolbarClassName={classes.richEditorToolbar}
        editorClassName={classes.richEditorBody}
        toolbar={{
          options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'remove', 'history'],
          inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
          blockType: { options: ['Normal', 'H3', 'H4'] },
          fontSize: { options: [11, 12, 13, 14, 16, 18, 20, 24] },
          list: { options: ['unordered', 'ordered', 'indent', 'outdent'] },
          textAlign: { options: ['left', 'center', 'right'] },
          link: { options: ['link', 'unlink'], defaultTargetOption: '_blank' },
          history: { options: ['undo', 'redo'] }
        }}
        toolbarCustomButtons={[<SpacingControl />]}
      />
    </Box>
  )
}

export default RichTextField
